import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export interface PageHeaderProps {
  title: React.ReactNode;
  refPill?: React.ReactNode;
  primaryAction?: React.ReactNode;
  className?: string;
}

export function PageHeader({ title, refPill, primaryAction, className }: PageHeaderProps) {
  const [portalNode, setPortalNode] = React.useState<HTMLElement | null>(null);
  const [rightPortalNode, setRightPortalNode] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    setPortalNode(document.getElementById("topbar-left-portal"));
    setRightPortalNode(document.getElementById("topbar-right-portal"));
  }, []);

  const titleBlock = (
    <div className={cn("flex items-center gap-3", portalNode && "pr-4 border-r border-border mr-4 last:border-r-0 last:mr-0 last:pr-0")}>
      <h1 className="text-[17px] font-semibold text-slate-900 m-0 leading-none">{title}</h1>
      {refPill ? (
        typeof refPill === "string" || typeof refPill === "number" ? (
          <span className="inline-flex items-center rounded-md border border-border bg-card px-2 py-0.5 text-xs font-medium text-primary">
            {refPill}
          </span>
        ) : (
          <div className="flex items-center">
            {refPill}
          </div>
        )
      ) : null}
    </div>
  );

  const actionBlock = primaryAction ? (
    <div className={cn("flex items-center [&_button]:h-8 [&_button]:text-sm", rightPortalNode && className)}>
      {primaryAction}
    </div>
  ) : null;

  // The topbar portals only exist inside AppShell. Render inline when they are
  // absent (unit tests, print, any non-shell route) so the page title and its
  // primary action are never silently dropped.
  if (!portalNode && !rightPortalNode) {
    return (
      <div className="flex items-center justify-between mb-4">
        {titleBlock}
        {actionBlock}
      </div>
    );
  }

  return (
    <>
      {/* At runtime the title portals into the TopBar <header>, which print.css
          hides — so also emit a print-only inline title inside the page content
          so printed pages aren't left untitled. */}
      <h1 className="hidden print:block text-xl font-bold text-slate-900 mb-4">{title}</h1>
      {portalNode ? createPortal(titleBlock, portalNode) : titleBlock}
      {actionBlock && (rightPortalNode ? createPortal(actionBlock, rightPortalNode) : actionBlock)}
    </>
  );
}
